<template>
  <v-card class="step-tab-panel" flat>
    <div class="assortment-table attributes-table switching-modelling-page d-flex flex-column">
      <v-container
        class="actions-container pa-2 flex-grow-0"
        data-id-e2e="switchingModellingActionsTopSection"
      >
        <v-row class="ma-0">
          <v-col class="pa-0 pr-6 mr-1 d-flex justify-start align-center br-1">
            <span class="font-weight-bold mr-1">{{ tkey('switchingModellingTitle') }}</span>
            <span class="pl-1 pr-1">{{ tkey('switchingModellingTitleNote') }}</span>

            <!-- Switching modelling tooltip -->
            <!-- TODO add tool guide link -->
            <DocsLink link="" />
          </v-col>
        </v-row>
      </v-container>

      <v-container
        class="actions-container pa-2 flex-grow-0"
        data-id-e2e="switchingModellingActionsBottomSection"
      >
        <v-row class="ma-0">
          <v-col class="pa-0 d-flex justify-start align-center mr-1 pr-6 br-1">
            <span class="font-weight-bold mr-1">{{ tkey('switchingModellingSubTitle') }}</span>
            <span class="pl-1 pr-1">{{ tkey('switchingModellingSubTitleNote') }}</span>
            <span class="rtls-border rtls-border--left ml-2 pl-2">
              {{ tkey('switchingModellingDataQuality') }}
            </span>
            <div class="square-quality good ml-3" />
            <span class="pl-1 pr-1">{{ $t('general.good') }}</span>
            <div class="square-quality medium ml-3" />
            <span class="pl-1 pr-1">{{ $t('general.medium') }}</span>
            <div class="square-quality poor ml-3" />
            <span class="pl-1 pr-1">{{ $t('general.poor') }}</span>
          </v-col>
        </v-row>
      </v-container>

      <div class="h-100 flex-grow-1">
        <AgGridVue
          data-id-e2e="switchingModellingGrid"
          class="h-100 w-100"
          :loading="isGridLoading"
          :row-data="switchingMetrics"
          :grid-options="gridOptions"
          @grid-ready="onGridReady"
        />
      </div>
    </div>
  </v-card>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { useClusteringStore } from '@/stores/clustering';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useSwitchingMatricesStore } from '@/stores/switching-matrices';
import cannGroupUtils from '@/utils/cann-groups';
import to from 'await-to-js';
import {
  each,
  filter,
  get,
  groupBy,
  includes,
  keyBy,
  last,
  map,
  partition,
  set,
  size,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import SimilarityBiasRenderer from '../renderers/SimilarityBiasRenderer.vue';
import SwitchingModellingMetricsRenderer from '../renderers/SwitchingModellingMetricsRenderer.vue';
import UseUnclusteredRenderer from '../renderers/UseUnclusteredRenderer.vue';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    SimilarityBiasRenderer,
    UseUnclusteredRenderer,
    SwitchingModellingMetricsRenderer,
  },

  setup() {
    const { tkey } = useInternationalization('switchingModellingPage');
    return { tkey };
  },

  data() {
    return {
      isGridLoading: false, // controls grid overlay
      switchingMetrics: [],
      headers: {
        cannGroupName: {
          headerName: '',
          field: 'cannGroupName',
          editable: false,
          width: 170,
          maxWidth: 170,
          cellClass: 'font-weight-bold mr-1 pa-2 pl-4',
        },
        totalProductCount: {
          headerName: this.tkey('productsCount'),
          field: 'totalProductCount',
          editable: false,
          width: 95,
          maxWidth: 95,
          headerClass: 'sub-header',
          cellClass: 'd-flex justify-end pa-2 pr-4 ag-cell-border-right',
        },
        similarityBias: {
          headerName: this.tkey('similarityBias'),
          field: 'similarityBias',
          editable: false,
          maxWidth: 220,
          cellRenderer: 'SimilarityBiasRenderer',
          cellRendererParams: () => ({
            clusters: this.clusters,
            onSelectionChange: this.onUnclusteredSelectionChanged,
          }),
          headerClass: 'sub-header',
          cellClass: 'px-2 grey-cell ag-cell-border-right',
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        useUnclustered: {
          headerName: this.tkey('useUnclustered'),
          field: 'useUnclustered',
          editable: false,
          width: 90,
          maxWidth: 90,
          menuTabs: [], // hides col menu
          cellStyle: { textAlign: 'right' },
          cellRenderer: 'UseUnclusteredRenderer',
          cellRendererParams: params => ({
            cannGroupId: params.data.cannGroupId,
            clusters: this.clusters,
            onSelectionChange: this.onUnclusteredSelectionChanged,
          }),
          headerClass: 'sub-header text-right',
          cellClass: 'grey-cell d-flex align-center justify-end pr-3',
        },
        metrics: {
          headerName: this.tkey('unclustered'),
          field: 'unclusteredMetrics',
          editable: false,
          maxWidth: 250,
          menuTabs: [],
          headerClass: 'ag-cell-border-left-bold ag-cell-border-right-bold sub-header',
          cellClass: 'px-1 ag-cell-border-left-bold ag-cell-border-right-bold',
          cellRenderer: 'SwitchingModellingMetricsRenderer',
          cellRendererParams: params => {
            // Products that appear in switching matrices are those with threshold linked transactions (baskets)
            // Undefined as the clusterId prop is optional, thus empty on unclustered matrices
            const productCountWithLinkedBaskets =
              params.data.cannGroupMatricesByCluster.undefined.original.productKeys;
            return {
              metrics: params.data.unclusteredMetrics,
              cannGroupId: params.data.cannGroupId,
              cannGroupName: params.data.cannGroupName,
              clusterName: this.tkey('unclustered'),
              productCountWithLinkedBaskets: productCountWithLinkedBaskets.length,
              totalProductCount: params.data.totalProductCount,
              storeCount: params.data.unclusteredStoreCount,
              onSetToUnclusteredChange: this.onUnclusteredSelectionChanged,
            };
          },
        },
        emptyColumn: {
          // empty column to add space after the last actual column
          resizable: false,
        },
      },

      initialHeaders: [], // Set in created hook
      gridOptions: {
        columnMenu: 'legacy',
        getRowId(row) {
          return `${row.data.cannGroupId}`;
        },
        suppressContextMenu: true,
        // hides blue border
        suppressCellFocus: true,
        tooltipShowDelay: 0,
        rowHeight: 50,
        headerHeight: 50,
        groupHeaderHeight: 60,
        overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'general.inProgress'
        )}</span>`,
        defaultColDef: {
          filter: true,
          suppressMovable: true,
          menuTabs: ['filterMenuTab'],
          sortable: false,
          editable: false,
          minWidth: 80,
          resizable: false,
        },
      },

      gridApi: null,

      isRunningCalculations: false,
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useClusteringStore, ['selectedScheme']),
    ...mapState(useScenarioProductsStore, ['scenarioProducts']),

    clusters() {
      return get(this.selectedScheme, 'clusters', []);
    },

    clustersById() {
      return groupBy(this.clusters, 'clusterId');
    },

    productsByCannGroup() {
      return groupBy(this.scenarioProducts, 'cannGroupId');
    },
  },

  async created() {
    await this.fetchScenarioClusters();
    await this.fetchScenarioProducts({ options: { shouldCommit: true } });
    await this.composeMetricsData();
    this.setAllInitialHeaders();

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.initialHeaders);
      this.gridApi.sizeColumnsToFit();
    }
  },

  methods: {
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),
    ...mapActions(useSwitchingMatricesStore, [
      'fetchScenarioSwitchingMatrices',
      'runSimulationSetToUnclustered',
      'undoSwitchingModellingDecisions',
    ]),
    ...mapActions(useScenarioProductsStore, ['fetchScenarioProducts']),

    onGridReady(params) {
      this.gridApi = params.api;

      if (!this.gridOptions.columnDefs)
        this.gridApi.setGridOption('columnDefs', this.initialHeaders);
    },

    async composeMetricsData() {
      // clear previous results
      this.switchingMetrics = [];
      // #1 - fetch and group all data
      const cannGroups = cannGroupUtils.getLeafNodes(this.selectedScenario.cannGroups);
      const validClusterIds = get(this.selectedScheme, 'clusters', []).map(c => c.clusterId);
      let switchingMatrices = await this.fetchScenarioSwitchingMatrices({
        where: {
          scenarioId: this.selectedScenario._id,
          // TODO: Simplify using NULL on $in AOV3-627
        },
      });
      // Ideally we'd pass NULL to query, however, repository utils sets NULLs to ObjectId
      // due to that, we remove unnecessary matrices here. TODO AOV3-627 - undo this hack
      switchingMatrices = switchingMatrices.filter(
        sm => !sm.clusterId || includes(validClusterIds, sm.clusterId)
      );

      const scenarioStoresInAnalysisInAssortment = filter(this.selectedScenario.stores, store => {
        return store.analysis || store.assortment;
      });

      const switchingMatricesByCannGroup = groupBy(switchingMatrices, 'cannGroupId');

      // #2 - for every cann group, build a row with metrics data based on switching matrices
      each(cannGroups, cannGroup => {
        const cannGroupMatrices = switchingMatricesByCannGroup[cannGroup.key];
        const cannGroupProducts = this.productsByCannGroup[cannGroup.key];
        const cannGroupMatricesByCluster = keyBy(cannGroupMatrices, 'clusterId');

        const row = {
          cannGroupId: cannGroup.key,
          cannGroupName: cannGroup.name,
          totalProductCount: size(cannGroupProducts), // total would be all products in a given cann group
          unclusteredStoreCount: size(scenarioStoresInAnalysisInAssortment),
          similarityBias: cannGroupMatrices[0].similarityBias, // all cann group matrices would have the same bias for now
          unclusteredMetrics: {},
          metricsByCluster: {},
          cannGroupMatricesByCluster,
          cannGroupMatrices,
        };

        // Fill in metrics data for every cann group
        each(cannGroupMatrices, matrix => {
          // Create unclustered metrics object for row first
          if (!matrix.clusterId) {
            row.unclusteredMetrics = matrix.metrics;
          } else {
            // Fill in metrics map for every cluster
            set(row.metricsByCluster, matrix.clusterId, matrix.metrics);
          }
        });

        this.switchingMetrics.push(row);
      });
    },

    createClusteredColumns() {
      // Use clusters in selected clustering scheme to initialise the cluster column group that renders on the table
      return map(this.clusters, ({ clusterName, clusterId, storeKeys, isNoDataCluster }) => {
        // Check if this is the final item in the clustered array
        // if so then add a right border class as it is the final column.
        const isLastCluster = last(this.clusters).clusterId === clusterId;

        return {
          ...this.headers.metrics,
          headerName: clusterName,
          headerClass: () => {
            return `${isLastCluster ? 'ag-cell-border-right' : ''} sub-header`;
          },
          cellClass: 'ag-cell-border-left ag-cell-border-right',
          field: `metricsByCluster.${clusterId}`,
          cellRendererParams: params => {
            // Products that appear in switching matrices are those with threshold linked transactions (baskets)
            const productCountWithLinkedBaskets = get(
              params.data.cannGroupMatricesByCluster[clusterId],
              'original.productKeys',
              []
            );
            return {
              metrics: params.data.metricsByCluster[clusterId],
              cannGroupId: params.data.cannGroupId,
              cannGroupName: params.data.cannGroupName,
              clusterId,
              clusterName,
              isNoDataCluster,
              productCountWithLinkedBaskets: productCountWithLinkedBaskets.length,
              totalProductCount: params.data.totalProductCount,
              storeCount: size(storeKeys),
              onSetToUnclusteredChange: this.onUnclusteredSelectionChanged,
            };
          },
        };
      });
    },

    setAllInitialHeaders() {
      // Generate column groups for each cluster group, including unclustered
      const clusteredColumns = this.createClusteredColumns();

      this.initialHeaders = [
        {
          headerName: this.tkey('cannGroups'),
          headerClass: 'pa-1',
          children: [
            this.headers.cannGroupName,
            this.headers.totalProductCount,
            this.headers.similarityBias,
            this.headers.useUnclustered,
          ],
        },
        {
          headerName: this.tkey('clusters'),
          headerClass: 'ag-cell-border-left-bold ag-cell-border-right-bold',
          // Column for unclustered metrics
          children: [this.headers.metrics],
        },
        // Generate column group for clusters
        {
          headerName: '',
          headerClass: 'border-left-md ag-cell-border-right',
          children: [...clusteredColumns],
        },
        {
          headerName: '',
          children: [this.headers.emptyColumn],
        },
      ];
    },

    async onUnclusteredSelectionChanged(payload) {
      this.isGridLoading = true;

      const [matricesToSetToUnclustered, itemsToUndo] = partition(payload, 'method');

      if (matricesToSetToUnclustered.length) {
        await this.setToUnclusterClustersCannGroup(matricesToSetToUnclustered);
      }
      if (itemsToUndo.length) {
        await this.revertChanges(itemsToUndo);
      }

      this.isGridLoading = false;
    },

    async setToUnclusterClustersCannGroup(calculations) {
      this.isRunningCalculations = true;
      // Compose payload for jobapi function
      const payload = {
        scenario_id: this.selectedScenario._id,
        calculations,
      };

      await this.runSimulationSetToUnclustered({ payload });
      // update original value to currently set value
      this.originalValue = this.similarityBias;
      await this.composeMetricsData();
      this.isRunningCalculations = false;
    },

    async revertChanges(itemsToUndo) {
      this.isRunningCalculations = true;

      await to(this.undoSwitchingModellingDecisions(itemsToUndo));
      await this.composeMetricsData();
      this.isRunningCalculations = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

// Remove bottom border of the second actions container
.assortment-table {
  .actions-container:nth-child(2) {
    border-bottom: none !important;
  }
}

// Style rows to all be white and have appropriate borders
:deep(.ag-row) {
  background-color: $assortment-table-white-bg-colour !important;

  border-bottom-color: $assortment-divider-colour-darker !important;
  border-bottom-width: 1px !important;
}

:deep(.ag-cell) {
  border-left-width: 0 !important;
  border-right-width: 0 !important;

  border-bottom-color: $assortment-divider-colour-darker !important;
  border-bottom-width: 1px !important;

  &.grey-cell {
    background-color: $assortment-background;
  }
}

:deep(.ag-header-cell-resize) {
  width: 2px;
}

:deep(.ag-cell-border-right) {
  border-right: 1px solid $assortment-divider-colour-darker !important;
}

:deep(.ag-cell-border-right-bold) {
  border-right: 3px solid $assortment-navbar-main-divider-colour !important;
}

:deep(.ag-cell-border-left-bold) {
  border-left: 3px solid $assortment-navbar-main-divider-colour !important;
}

:deep(.sub-header span) {
  text-wrap: wrap;
  font-weight: normal;
}

.square-quality {
  width: 15px;
  height: 15px;

  &.good {
    background-color: $assortment-chip-color;
  }

  &.medium {
    background: $assortment-sales-confidence-score-orange;
  }
  &.poor {
    background: $assortment-sales-confidence-score-red;
  }
}
</style>
